import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Custom lock function using Web Locks API to prevent refresh token race conditions
// across multiple tabs. Without this, multiple tabs can try to refresh the same token
// simultaneously, causing "Invalid Refresh Token: Already Used" errors.
const navigatorLock = async <R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    // Fallback for browsers without Web Locks API (Safari < 15.4)
    console.warn('[Supabase] Web Locks API not available, falling back to no-lock mode');
    return fn();
  }

  // If acquireTimeout is 0 or negative, don't use a timeout (wait indefinitely)
  // Supabase SDK passes -1 or 0 when it doesn't want a timeout
  if (acquireTimeout <= 0) {
    return await navigator.locks.request(name, async () => fn());
  }

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), acquireTimeout);

  try {
    return await navigator.locks.request(
      name,
      { signal: abortController.signal },
      async () => {
        clearTimeout(timeoutId);
        return fn();
      }
    );
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Lock acquisition timed out after ${acquireTimeout}ms`);
    }
    throw error;
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Persist session to localStorage for web and PWA
    persistSession: true,
    // Use localStorage for session storage (works for both web and PWA)
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    // Automatically refresh token before expiry
    autoRefreshToken: true,
    // Detect session from URL (important for OAuth callbacks)
    detectSessionInUrl: true,
    // Flow type for PKCE (more secure, default in newer SDK)
    flowType: 'pkce',
    // Use Web Locks API to prevent refresh token race conditions across tabs
    // This ensures only one tab refreshes the token at a time
    lock: navigatorLock,
    // Enable debug logging in development to track auth issues
    debug: import.meta.env.DEV,
  },
});
